import { useCaseModuleModalRootActionsStore } from '@/features/cases/_shared/components/case-modal-root/case-modal-root.store';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  deleteLabelChatValidationSchema,
  TDeleteLabelChatValidation,
  useDeleteLabelChatMutation,
} from '../../../api/delete-label-chat.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_LABEL_CHAT_QUERY_KEY } from '../../../api/get-list-label-chat.api';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
  ModalCloseButton,
} from '@/components/_ui/modal';
import { handleDomBubbleEvent } from '@/utils/dom';
import { Spinner } from '@/components/_ui/spinner';

export type TDeleteLabelChatModalType = 'DeleteLabelChatModal';
export interface IDeleteLabelChatModalProps {
  labelChatId: string;
}

const DeleteLabelChatModal = memo(({ labelChatId }: IDeleteLabelChatModalProps) => {
  const caseModuleModalRootActions = useCaseModuleModalRootActionsStore();
  const showToast = useCustomToast();

  const isOpenDeleteLabelChatModal = useMemo(() => {
    return caseModuleModalRootActions.checkIsOpenModal('DeleteLabelChatModal');
  }, [caseModuleModalRootActions]);

  const form = useForm<TDeleteLabelChatValidation>({
    resolver: zodResolver(deleteLabelChatValidationSchema),
    mode: 'onChange',
    defaultValues: {
      labelChatId: labelChatId || '',
    },
  });

  useEffect(() => {
    if (labelChatId) {
      form.reset({
        labelChatId: labelChatId || '',
      });
    }
  }, [labelChatId, form]);

  const mutationDeleteLabelChat = useDeleteLabelChatMutation({
    mutationConfigOptions: {
      onError: (_error) => {
        const errorMessage = axiosExtractErrorMessage(_error) || 'Error delete label chat, please try again';
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        Promise.all([queryClient.invalidateQueries({ queryKey: [GET_LIST_LABEL_CHAT_QUERY_KEY] })]);
        caseModuleModalRootActions.closeModal('DeleteLabelChatModal');
        showToast({
          message: 'Label chat deleted successfully!',
          type: 'success',
        });
      },
    },
  });

  const handleSubmitDeleteLabelChat = useCallback(
    (data: TDeleteLabelChatValidation) => {
      mutationDeleteLabelChat.mutate({
        labelChatId: data?.labelChatId || '',
      });
    },
    [mutationDeleteLabelChat]
  );

  const handleClose = useCallback(() => {
    caseModuleModalRootActions.closeModal('DeleteLabelChatModal');
  }, [caseModuleModalRootActions]);

  return (
    <Modal isOpen={isOpenDeleteLabelChatModal} lockBodyScroll onClose={handleClose} className="p-[32px] w-[500px]">
      <ModalHeader>
        <ModalTitle>Delete Label Chat</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitDeleteLabelChat)(e);
          }}
          id="delete-label-chat-form"
          className="grid gap-[16px]"
        >
          <span className="text-[14px] font-normal text-[#9D9D9C]">Do you want to delete this label chat?</span>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="delete-label-chat-form" type="submit" disabled={mutationDeleteLabelChat.isPending}>
          {mutationDeleteLabelChat.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null} Delete
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
DeleteLabelChatModal.displayName = 'DeleteLabelChatModal';
export default DeleteLabelChatModal;
